import i18n from 'i18next';
import {
  PROLIX_ERROR_IDENTIFIER,
  PROLIX_ERROR_MESSAGE
} from '~/common/types/prolixApplicationError';
import { isWebshopApiErrorException } from '~/common/types/webshopApiError';

type CartStateMachineErrorOptions = {
  cause: unknown;
  status?: number;
  message: string;
  prolixErrorIdentifier?: string;
  traceId?: string;
};

export class CartStateMachineError extends Error {
  readonly cause: unknown;
  readonly status?: number;
  readonly [PROLIX_ERROR_IDENTIFIER]?: string;
  readonly traceId?: string;

  constructor({
    cause,
    status,
    message,
    prolixErrorIdentifier,
    traceId
  }: CartStateMachineErrorOptions) {
    super(message);
    this.name = 'CartStateMachineError';
    this.cause = cause;
    this.status = status;
    this[PROLIX_ERROR_IDENTIFIER] = prolixErrorIdentifier;
    this.traceId = traceId;
  }
}

export function normalizeCartError(error: unknown): CartStateMachineError {
  const fallbackMessage = `${i18n.t('Beim Verarbeiten des Warenkorbs ist ein unerwarteter Fehler aufgetreten')}!`;

  if (isWebshopApiErrorException(error)) {
    const apiError = error.apiError;
    return new CartStateMachineError({
      cause: error,
      status: apiError.status,
      message: apiError.message || fallbackMessage,
      prolixErrorIdentifier: apiError.payload?.prolixErrorIdentifier ?? undefined,
      traceId: apiError.traceId
    });
  }

  const maybeProlix = error as
    | {
        message?: string;
        [PROLIX_ERROR_IDENTIFIER]?: string;
        [PROLIX_ERROR_MESSAGE]?: string;
        traceId?: string;
      }
    | null
    | undefined;
  return new CartStateMachineError({
    cause: error,
    status: getCartErrorStatus(error),
    message: maybeProlix?.[PROLIX_ERROR_MESSAGE] ?? maybeProlix?.message ?? fallbackMessage,
    prolixErrorIdentifier: maybeProlix?.[PROLIX_ERROR_IDENTIFIER],
    traceId: maybeProlix?.traceId
  });
}

export function isLoginExpiredError(error: unknown): boolean {
  const status = getCartErrorStatus(error);
  return status === 401 || status === 403;
}

function getCartErrorStatus(error: unknown, seen = new Set<unknown>()): number | undefined {
  if (isWebshopApiErrorException(error)) {
    return error.apiError.status;
  }
  if (typeof error !== 'object' || error === null || seen.has(error)) {
    return undefined;
  }

  seen.add(error);
  const candidate = error as {
    status?: unknown;
    response?: { status?: unknown };
    cause?: unknown;
  };
  if (typeof candidate.status === 'number') {
    return candidate.status;
  }
  if (typeof candidate.response?.status === 'number') {
    return candidate.response.status;
  }
  return getCartErrorStatus(candidate.cause, seen);
}
